import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Home, CalendarDays, Utensils, Images, Instagram, KeyRound, Settings, ShoppingBag, Ticket, UserRound } from "lucide-react";
import { useEventContent } from "@/context/EventContentContext";
import BrandMark from "@/components/BrandMark";
import SocialLinks, { externalHref } from "@/components/SocialLinks";

const topLinks = [
  { to: "/", label: "Inicio", slug: "inicio" },
  { to: "/evento", label: "Evento", slug: "evento" },
  { to: "/programa", label: "Programa", slug: "programa" },
  { to: "/chefs", label: "Chefs", slug: "chefs" },
  { to: "/menu", label: "Menú", slug: "menu" },
  { to: "/ubicacion", label: "Ubicación", slug: "ubicacion" },
  { to: "/anuncios", label: "Anuncios", slug: "anuncios" },
  { to: "/galeria", label: "Galería", slug: "galeria" },
  { to: "/faq", label: "FAQ", slug: "faq" },
  { to: "/staff", label: "Staff", slug: "staff" },
];

const bottomNav = [
  { to: "/", label: "Inicio", slug: "inicio", icon: Home },
  { to: "/programa", label: "Programa", slug: "programa", icon: CalendarDays },
  { to: "/menu", label: "Menú", slug: "menu", icon: Utensils },
  { to: "/galeria", label: "Galería", slug: "galeria", icon: Images },
  { to: "/staff", label: "Staff", slug: "staff", icon: KeyRound },
];

export default function Layout({ children }) {
  const location = useLocation();
  const { event } = useEventContent();
  const [ticketMenuOpen, setTicketMenuOpen] = useState(false);
  const ticketMenuRef = useRef(null);
  const isStaff = location.pathname.startsWith("/staff");
  const instagramTicketUrl = externalHref(event.social?.instagramUrl);

  useEffect(() => {
    setTicketMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const closeMenu = (event) => {
      if (event.key === "Escape" || (event.type === "pointerdown" && !ticketMenuRef.current?.contains(event.target))) {
        setTicketMenuOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeMenu);
    document.addEventListener("keydown", closeMenu);
    return () => {
      document.removeEventListener("pointerdown", closeMenu);
      document.removeEventListener("keydown", closeMenu);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] text-[var(--brand-ink)] pb-nav">
      {/* Top navigation — desktop */}
      <header className="hidden xl:block sticky top-0 z-40 bg-[var(--brand-bg)]/95 backdrop-blur border-b border-[var(--brand-border)]">
        <div className="container-cg flex items-center justify-between h-20">
          <Link to="/" data-testid="top-nav-logo" className="flex items-baseline gap-3">
            <BrandMark event={event} textClassName="font-serif-cg text-2xl tracking-tight" />
            <span className="overline text-[var(--brand-muted)]">{event.edition}</span>
          </Link>
          <nav className="flex items-center gap-7">
            {topLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`top-nav-${l.slug}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide link-underline ${
                    isActive ? "text-[var(--brand-primary)] font-semibold" : "text-[var(--brand-ink)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <NavLink to="/mi-cuenta" className="btn-secondary-cg" aria-label="Mi cuenta"><UserRound size={15} /> Mi cuenta</NavLink>
            <NavLink to="/admin" data-testid="top-nav-admin" className="btn-primary-cg"><Settings size={14} /> Admin</NavLink>
          </div>
        </div>
      </header>

      {/* Mobile header (compact) */}
      <header className="xl:hidden sticky top-0 z-40 bg-[var(--brand-bg)]/95 backdrop-blur border-b border-[var(--brand-border)]">
        <div className="container-cg flex items-center justify-between h-14">
          <Link to="/" className="flex items-baseline gap-2">
            <BrandMark
              event={event}
              imageClassName="h-9 max-w-[150px]"
              textClassName="font-serif-cg text-lg tracking-tight"
            />
          </Link>
          <Link to="/mi-cuenta" aria-label="Mi cuenta" className="inline-flex h-10 w-10 items-center justify-center text-[var(--brand-primary)]"><UserRound size={20} /></Link>
        </div>
      </header>

      <main className={isStaff ? "" : "anim-fade-up"}>{children}</main>

      {/* Footer */}
      <footer className="hidden xl:block border-t border-[var(--brand-border)] mt-20">
        <div className="container-cg py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <BrandMark event={event} imageClassName="h-14 max-w-[220px]" textClassName="font-serif-cg text-2xl" />
            <div className="overline text-[var(--brand-muted)] mt-2">{event.edition}</div>
            <p className="text-sm text-[var(--brand-muted)] mt-4 max-w-xs">{event.tagline}</p>
          </div>
          <div>
            <div className="overline mb-3">Contacto</div>
            <p className="text-sm">{event.contactEmail}</p>
            <p className="text-sm">{event.contactPhone}</p>
          </div>
          <div>
            <div className="overline mb-3">Síguenos</div>
            <SocialLinks social={event.social} className="flex flex-col items-start gap-3 text-sm" />
          </div>
        </div>
        <div className="container-cg py-6 text-xs text-[var(--brand-muted)] border-t border-[var(--brand-border)]">
          © {new Date().getFullYear()} {event.name} · {event.subtitle} · {event.edition}
        </div>
      </footer>

      {event.ticketButton?.enabled ? (
        <div ref={ticketMenuRef} className="fixed right-4 bottom-20 md:right-6 md:bottom-6 z-40">
          {ticketMenuOpen ? (
            <div role="menu" className="absolute bottom-full right-0 mb-2 w-64 overflow-hidden border border-[var(--brand-border)] bg-white shadow-xl">
              <Link
                to="/boletos"
                role="menuitem"
                className="flex items-center gap-3 px-4 py-4 text-sm text-[var(--brand-ink)] hover:bg-[var(--brand-surface-alt)]"
              >
                <ShoppingBag size={18} className="text-[var(--brand-primary)]" />
                <span><strong className="block">Compra online</strong><span className="text-xs text-[var(--brand-muted)]">Pago seguro con Stripe</span></span>
              </Link>
              {instagramTicketUrl ? (
                <a
                  href={instagramTicketUrl}
                  target="_blank"
                  rel="noreferrer"
                  role="menuitem"
                  className="flex items-center gap-3 border-t border-[var(--brand-border)] px-4 py-4 text-sm text-[var(--brand-ink)] hover:bg-[var(--brand-surface-alt)]"
                >
                  <Instagram size={18} className="text-[var(--brand-primary)]" />
                  <span><strong className="block">Comprar por Instagram</strong><span className="text-xs text-[var(--brand-muted)]">Hablar con el equipo</span></span>
                </a>
              ) : null}
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => setTicketMenuOpen((open) => !open)}
            aria-expanded={ticketMenuOpen}
            aria-haspopup="menu"
            data-testid="floating-ticket-button"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-primary)] text-white px-4 py-3 text-xs font-semibold shadow-lg hover:bg-[var(--brand-primary-hover)] transition-colors"
          >
            <Ticket size={16} aria-hidden="true" />
            <span>{event.ticketButton.label || "Comprar boletos"}</span>
          </button>
        </div>
      ) : null}

      {/* Bottom navigation — mobile */}
      <nav
        data-testid="bottom-nav"
        className="xl:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-[var(--brand-border)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="grid grid-cols-5">
          {bottomNav.map(({ to, label, slug, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={to === "/"}
                data-testid={`bottom-nav-${slug}`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center py-2.5 gap-0.5 text-[10px] tracking-wider ${
                    isActive ? "text-[var(--brand-primary)]" : "text-[var(--brand-muted)]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon size={20} strokeWidth={isActive ? 2.4 : 1.6} />
                    <span className="uppercase">{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
